import { useCallback, useState } from "react";
import { Button, useTheme } from "@mui/material";
import { isEqual } from "lodash-es";
import Editor from "@monaco-editor/react";

import TitledCard from "../basic/TitledCard";
import { useEditedColor } from "../../../hooks/useEditedStyles";

import { ExtraTemplateItemProps } from "./types";
import { useDevice } from "../../../utils/DeviceProvider";
import { Config } from "../../../utils/types";
import { uuidv4 } from "../../../utils/utils";

const JsonEditor = ({ configKey, title }: ExtraTemplateItemProps) => {
  const [hasError, setHasError] = useState(false);
  const [editorKey, setEditorKey] = useState(uuidv4());
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const theme = useTheme();

  const onChange = useCallback(
    (v?: string) => {
      if (!v) {
        currentValue[configKey] = null;
        setCurrentValue(currentValue);
        setHasError(false);
        return;
      }
      try {
        const parsed = JSON.parse(v as string);
        setHasError(false);
        currentValue[configKey] = parsed;
        setCurrentValue(currentValue);
      } catch (err) {
        setHasError(true);
      }
    },
    [configKey, currentValue, setCurrentValue]
  );

  const resetValue = useCallback(() => {
    if (!config) {
      return;
    }
    const saved = config.toolboxConfig[configKey] ?? null;
    currentValue[configKey] = saved;
    setCurrentValue(currentValue);
    setEditorKey(uuidv4());
  }, [config, configKey, currentValue, setCurrentValue]);

  const edited = !isEqual(config?.toolboxConfig[configKey], currentValue?.[configKey]);

  return (
    <TitledCard
      title={title}
      color={hasError ? theme.palette.error.dark : edited ? editedBackground : undefined}
      contrastColor={edited ? editedText : undefined}
      headerSx={{ height: 48 }}
      postfix={
        edited ? (
          <Button size="small" sx={{ my: 0, mx: 1, px: 2, color: editedText }} onClick={resetValue}>
            Reset value
          </Button>
        ) : null
      }
    >
      <Editor
        key={editorKey}
        height="400px"
        defaultLanguage="json"
        defaultValue={currentValue?.[configKey] ? JSON.stringify(currentValue[configKey], null, 2) : ""}
        onChange={onChange}
        options={{ minimap: { enabled: false } }}
      />
    </TitledCard>
  );
};

export const isEdited = (currentValue: any, config: Config | null, key: string) => !isEqual(currentValue?.[key], config?.toolboxConfig?.[key]);

export default JsonEditor;
